/**
 * couleur — lecture d'une couleur saisie ou collée par un utilisateur.
 *
 * Sorti pour être éprouvé sans navigateur (cf. `banc/banc-couleur.mjs`) : ce code
 * reçoit ce que les gens collent, et ce qu'on colle n'est presque jamais propre.
 * Un `#F59E0B` copié depuis une charte arrive avec une espace insécable, un code
 * lu dans un PDF arrive sans dièse, un code venu d'Illustrator arrive en
 * minuscules, et une valeur relue d'un style calculé arrive en `rgb(245, 158, 11)`.
 */
import { versRgb } from './regulatoryLisibilite';

/**
 * Normalise une couleur saisie en `#RRGGBB` majuscule, ou renvoie null.
 *
 * Tolère : le dièse absent, la forme courte à trois chiffres, les minuscules, les
 * espaces autour, un `rgb()` / `rgba()` complet, et les espaces INSÉCABLES qu'un
 * copier-coller depuis une page web ou un document traîne derrière lui — ceux-là
 * ne se voient pas à la relecture, et un `trim()` ordinaire ne les enlève pas.
 *
 * Le majuscule est une convention de sortie, pour que deux codes identiques se
 * ressemblent dans l'interface. La comparaison, elle, reste insensible à la casse.
 */
export function normaliserHex(saisie) {
    if (typeof saisie !== 'string') return null;
    // \s ne couvre pas U+00A0 (espace insécable) dans toutes les implémentations,
    // et U+200B (espace sans chasse) jamais : on les retire explicitement.
    const nettoye = saisie.replace(/[\s ​]/g, '');
    if (!nettoye) return null;

    const court = /^#?([0-9a-f]{3})$/i.exec(nettoye);
    if (court) {
        const h = court[1];
        return ('#' + h[0] + h[0] + h[1] + h[1] + h[2] + h[2]).toUpperCase();
    }
    const long = /^#?([0-9a-f]{6})$/i.exec(nettoye);
    if (long) return ('#' + long[1]).toUpperCase();

    // rgb() / rgba() — ce que rend un style calculé, et ce que colle un inspecteur
    // de navigateur. L'alpha est ignoré : les calques portent leur opacité à part.
    const rgb = versRgb(nettoye);
    if (rgb) return versHex(rgb);

    return null;
}

/** { r, g, b } → `#RRGGBB` majuscule. */
export function versHex({ r, g, b }) {
    const deuxChiffres = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
    return ('#' + deuxChiffres(r) + deuxChiffres(g) + deuxChiffres(b)).toUpperCase();
}

/** Deux couleurs désignent-elles la même teinte, quelle que soit leur écriture ? */
export function memeCouleur(a, b) {
    const na = normaliserHex(a);
    const nb = normaliserHex(b);
    return na !== null && na === nb;
}

/**
 * La pipette est-elle disponible ici ?
 *
 * Elle repose sur l'API EyeDropper du navigateur, qui prélève un pixel n'importe
 * où à l'écran — donc aussi bien sur le canevas que dans une maquette ouverte à
 * côté. Chrome et Edge l'ont ; Firefox et Safari ne l'ont pas. Plutôt que de faire
 * disparaître le bouton d'un poste à l'autre sans explication, on le laisse en
 * place et on le désactive en disant pourquoi.
 */
export function pipetteDisponible() {
    return typeof window !== 'undefined' && typeof window.EyeDropper === 'function';
}

/**
 * Ouvre la pipette et rend la couleur prélevée, ou null si l'utilisateur renonce.
 *
 * Ne lève jamais : un abandon (Échap) rejette la promesse de l'API, et c'est un
 * geste ordinaire, pas une erreur. L'import est déjà au niveau de l'objet global,
 * il n'y a donc rien de fragile à protéger au-delà de l'appel lui-même.
 */
export async function preleverCouleur() {
    if (!pipetteDisponible()) return null;
    try {
        const { sRGBHex } = await new window.EyeDropper().open();
        return normaliserHex(sRGBHex);
    } catch (_) {
        return null;   // Échap, ou refus du navigateur
    }
}
